import { amountComposer } from "../../design-system/components/amount.ts";
import { aperture } from "../../design-system/components/aperture.ts";
import { button } from "../../design-system/components/button.ts";
import { card, detailList } from "../../design-system/components/card.ts";
import { confirmDialog } from "../../design-system/components/dialog.ts";
import { loadingBlock, notice, statusBadge } from "../../design-system/components/feedback.ts";
import { icon } from "../../design-system/components/icon.ts";
import { stepIndicator } from "../../design-system/components/steps.ts";
import { el, replaceChildren } from "../../design-system/dom.ts";
import { formatMoney } from "../../design-system/format/money.ts";
import { statusPresentation } from "../../design-system/format/status.ts";
import {
  claimWindowHours,
  clearDraft,
  commitDraft,
  draftAmount,
  type ReferenceTransfer,
  readTransferStatus,
  resolveDraftCurrency,
  setDraft,
} from "../reference-transfer.ts";
import type { Navigate, RouteView } from "../router.ts";
import type { ShellState } from "../state.ts";
import type { Tenant } from "../tenants.ts";

/**
 * The sender flow: enter an amount, review it, see the resulting transfer.
 *
 * ## Why this flow and not a dashboard
 *
 * `CLAUDE.md` § Regulatory boundary forbids building transaction history,
 * statements, spend summaries, balance display, or "any feature that aggregates
 * a user's financial activity across time or banks" — and forbids implementing
 * a workaround that "avoids the letter of this rule while achieving the same
 * effect". An activity list or transfer history built from synthetic data would
 * be exactly that: the prohibited product, assembled and waiting for real data.
 *
 * So the interface shows one transfer, the one being made. That is not a
 * reduced version of a banking app; it is the shape this architecture actually
 * has, and building it honestly produces a better product screen than a
 * dashboard would have.
 *
 * ## What is deliberately not implemented
 *
 * The bank authorization step is presentational only. BA-1 is unresolved, the
 * K-topology is undecided, and PV-03/PV-04 have not been run, so there is no
 * approved handoff to implement. Claim mechanics are absent for the same
 * reason at PV-16. Neither is stubbed behind a button that looks like it works.
 */

const LOCALE = "en-GB";
const STEPS = ["Amount", "Review", "Status"] as const;

/**
 * How copy refers to the authorising institution.
 *
 * For a bank tenant this is the bank's name. For the Nura reference identity it
 * is **"your bank"** — never "Nura". Interpolating the tenant name blindly
 * produced sentences like "Nura authenticates you through its own security" on
 * the reference build, which inverts the authority `CLAUDE.md` § Product
 * boundary fixes: the bank authenticates and authorises, and Nura never does.
 * A styling default must not be able to rewrite who is in charge.
 */
function bankLabel(tenant: Tenant): string {
  return tenant.isReference === true ? "your bank" : tenant.name;
}

/** `bankLabel` at the start of a sentence. */
function bankLabelLeading(tenant: Tenant): string {
  const label = bankLabel(tenant);
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

/**
 * How copy refers to the orchestration layer itself.
 *
 * `attribution: "none"` means no customer-facing Nura attribution — and body
 * copy is customer-facing. Hiding the wordmark while sentences still read
 * "Nura never approves a transfer" would be a half-measure that names the
 * vendor anyway, which is precisely what a partner buying a white-label
 * product did not buy. The statements themselves are unchanged; only the
 * subject is.
 */
function serviceLeading(tenant: Tenant): string {
  return tenant.attribution === "none" ? "This service" : "Nura";
}

/** The same subject as a grammatical object — "typed into …". */
function serviceObject(tenant: Tenant): string {
  return tenant.attribution === "none" ? "this app" : "Nura";
}

/**
 * The same subject mid-sentence.
 *
 * "Nura" is a proper noun and keeps its capital wherever it appears;
 * "This service" is not, and lowercases. Lowercasing the leading form
 * unconditionally produced "because nura never receives them".
 */
function serviceMid(tenant: Tenant): string {
  return tenant.attribution === "none" ? "this service" : "Nura";
}

function pageHeader(eyebrow: string, title: string, lede?: string): HTMLElement {
  return el("div", { class: "nura-page-header" }, [
    el("p", { class: "nura-eyebrow", text: eyebrow }),
    el("h1", { class: "nura-page-header__title", text: title }),
    lede === undefined ? undefined : el("p", { class: "nura-lede", text: lede }),
  ]);
}

/**
 * A supporting assurance.
 *
 * Three short statements of what Nura does not do. They earn their place by
 * filling the desktop column with the product's actual argument rather than
 * with decoration — and each one is a claim `CLAUDE.md` already binds us to.
 */
function assurance(
  glyph: "shield-check" | "lock" | "aperture",
  title: string,
  text: string,
): HTMLElement {
  return el("li", { class: "nura-assurances__item" }, [
    el("span", {
      class: "nura-assurances__mark",
      staticHtml: glyph === "aperture" ? aperture(18, { weight: 10 }) : icon(glyph, 18),
    }),
    el("div", {}, [
      el("p", { class: "nura-assurances__title", text: title }),
      el("p", { class: "nura-assurances__text", text }),
    ]),
  ]);
}

/** One step in the "what happens next" list. */
function nextStep(text: string): HTMLElement {
  return el("li", {}, [
    el("span", { class: "nura-next-steps__marker", staticHtml: icon("arrow-right", 14) }),
    el("span", { text }),
  ]);
}

/** One term/value pair in the compact meta row under a hero amount. */
function metaItem(term: string, value: string): HTMLElement {
  const mono = term === "Bank reference";
  return el("div", { class: "nura-meta__item" }, [
    el("dt", { class: "nura-meta__term", text: term }),
    el("dd", {
      class: mono ? "nura-meta__value nura-mono" : "nura-meta__value",
      text: value,
    }),
  ]);
}

/**
 * A reference-build limitation, stated quietly.
 *
 * The limitation must be visible and must not be dressed up as a feature — but
 * it also should not be the loudest object on a screen about sending money. A
 * ruled note reads as demo-mode information; the attention notice it replaced
 * read as a warning about the transfer itself.
 */
function referenceNote(state: ShellState, full: string, brief: string): HTMLElement {
  return el("p", { class: "nura-reference-note", attrs: { role: "note" } }, [
    el("span", {
      class: "nura-reference-note__label",
      text: state.isDemo ? "Not implemented" : "Reference build",
    }),
    el("span", { text: state.isDemo ? brief : full }),
  ]);
}

function actions(children: readonly HTMLElement[]): HTMLElement {
  return el("div", { class: "nura-actions" }, children);
}

// ---------------------------------------------------------------------------
// Step 1 — amount
// ---------------------------------------------------------------------------

export function composeRoute(state: ShellState, navigate: Navigate): RouteView {
  const HOW_IT_WORKS =
    "Digits go straight into minor units — there is no decimal key, and no text field to paste into.";

  const hint = el("p", { class: "nura-hint", attrs: { id: "nura-advance-hint" } });

  const advance = button({
    label: "Review transfer",
    variant: "primary",
    block: true,
    icon: "arrow-right",
    iconPosition: "end",
    onClick: () => {
      navigate("/review");
    },
  });

  function sync(): void {
    const ready = draftAmount() > 0n;
    advance.disabled = !ready;
    // A disabled button with no explanation is a dead end, so the hint says
    // what unlocks it — and stops saying it once the amount is there.
    //
    // The minor-unit sentence explains an implementation choice to engineers
    // and means nothing to anyone else, so it is reference-mode only. The
    // sentence that unlocks the button is not: it survives both modes,
    // because a disabled control still has to explain itself.
    advance.setAttribute("aria-describedby", "nura-advance-hint");
    const prompt = ready ? "" : "Enter an amount to continue.";
    hint.textContent = state.isDemo ? prompt : `${prompt} ${HOW_IT_WORKS}`.trim();
    hint.hidden = hint.textContent === "";
  }

  const composer = amountComposer({
    currency: resolveDraftCurrency(state.tenant),
    locale: LOCALE,
    currencies: state.tenant.currency.supportedCurrencies,
    action: advance,
    onChange: (minorUnits, currency) => {
      setDraft(minorUnits, currency);
      sync();
    },
  });

  sync();

  /*
   * Desktop is a two-column composition rather than the mobile column centred
   * in a wide page: the reasoning sits on the left, the instrument on the
   * right. That fills 1100 and 1440 deliberately instead of leaving the canvas
   * looking unfinished, and it collapses to a single column on a phone without
   * either half being rearranged.
   */
  const node = el("div", { class: "nura-stack" }, [
    stepIndicator({ steps: [...STEPS], current: 0 }),
    el("div", { class: "nura-flow" }, [
      el("div", { class: "nura-flow__intro" }, [
        pageHeader(
          state.tenant.name,
          "Enter an amount",
          "Your bank authorises and holds the transfer. Whoever you send it to claims it with a link — no account with your bank needed.",
        ),
        el("ul", { class: "nura-assurances" }, [
          assurance(
            "shield-check",
            "Your bank authorises it",
            `${serviceLeading(state.tenant)} never approves a transfer and never moves money.`,
          ),
          assurance(
            "lock",
            "You stay in your conversation",
            "The claim link is placed in your message; you send it yourself.",
          ),
          assurance(
            "aperture",
            "No account details here",
            `${serviceLeading(state.tenant)} holds no recipient name, account or destination.`,
          ),
        ]),
      ]),
      el("div", { class: "nura-flow__panel" }, [composer.root, hint]),
    ]),
  ]);

  return {
    node,
    destroy(): void {
      composer.destroy();
    },
  };
}

// ---------------------------------------------------------------------------
// Step 2 — review and the bank's authorization step
// ---------------------------------------------------------------------------

export function reviewRoute(state: ShellState, navigate: Navigate): RouteView {
  const currency = resolveDraftCurrency(state.tenant);

  if (draftAmount() === 0n) {
    // Reached by deep link or a reload, which drops the memory-only draft.
    navigate("/");
  }

  const cancel = confirmDialog({
    title: "Discard this transfer?",
    text: "The amount you entered will be cleared. Nothing has been sent to your bank.",
    confirmLabel: "Discard",
    cancelLabel: "Keep editing",
    tone: "danger",
    onConfirm: () => {
      clearDraft();
      navigate("/");
    },
  });

  /*
   * The amount leads the page rather than sitting in the first of several
   * white cards. Read top to bottom this is now: what you are sending, who
   * decides, and the one button that continues — with everything else quieter
   * than all three.
   */
  const hero = el("div", { class: "nura-hero" }, [
    el("p", { class: "nura-eyebrow", text: "Amount to send" }),
    el("p", {
      class: "nura-hero__value",
      text: formatMoney(draftAmount(), currency, LOCALE),
    }),
    el("dl", { class: "nura-meta" }, [
      metaItem("Currency", currency),
      metaItem("Sending bank", bankLabelLeading(state.tenant)),
      metaItem("Claim window", `${claimWindowHours()} hours`),
    ]),
  ]);

  const authorization = card(
    {
      title: "Authorisation by your bank",
      note: `${bankLabelLeading(state.tenant)} decides whether this transfer goes ahead.`,
      headingLevel: "h2",
      aside: el("span", {
        class: "nura-card__mark",
        staticHtml: icon("shield-check", 20),
      }),
    },
    [
      el("p", {
        class: "nura-lede",
        text: `${serviceLeading(state.tenant)} never authorises a transfer, never moves money and never sees your account. ${bankLabelLeading(state.tenant)} authenticates you through its own security, applies its own limits and checks, and issues the claim link.`,
      }),
      el("p", {
        class: "nura-hint",
        text: `You stay in your conversation while that happens. Your bank's credentials are never typed into ${serviceObject(state.tenant)}.`,
      }),
    ],
  );

  const node = el("div", { class: "nura-stack" }, [
    stepIndicator({ steps: [...STEPS], current: 1 }),
    hero,
    authorization,
    actions([
      button({
        label: "See the resulting transfer",
        variant: "primary",
        block: true,
        icon: "arrow-right",
        iconPosition: "end",
        onClick: () => {
          // The one place the draft becomes the transfer the status screen
          // describes. Without this the two screens read different sources.
          commitDraft();
          navigate("/status");
        },
      }),
      button({
        label: "Back to amount",
        variant: "secondary",
        block: true,
        icon: "arrow-left",
        onClick: () => {
          navigate("/");
        },
      }),
      button({
        label: "Discard",
        variant: "ghost",
        block: true,
        onClick: () => {
          cancel.open();
        },
      }),
    ]),
    referenceNote(
      state,
      "Authorisation is not implemented in this reference build. Its mechanism is set by each bank and depends on platform validation that is still open, so nothing here approves, declines, or stands in for a bank decision.",
      "Bank authorisation is not implemented. Nothing here approves or declines a transfer.",
    ),
    cancel.root,
  ]);

  return { node };
}

// ---------------------------------------------------------------------------
// Step 3 — the bank-reported transfer state
// ---------------------------------------------------------------------------

export function statusRoute(state: ShellState, _navigate: Navigate): RouteView {
  const region = el("div", { class: "nura-stack" }, [loadingBlock("Loading transfer status")]);

  let live = true;

  void readTransferStatus(state.tenant).then((transfer: ReferenceTransfer) => {
    if (!live) return;
    const presentation = statusPresentation(transfer.status);

    /*
     * The confident moment. The aperture closes on a transfer the bank
     * approved — the one place in the product where motion marks an event
     * rather than decorating a transition, and the same shape the future
     * keyboard can show in a single compact strip.
     */
    const ready = el("div", { class: "nura-ready" }, [
      el("span", { class: "nura-ready__mark" }, [
        el("span", {
          class: "nura-ready__ring",
          staticHtml: aperture(56, { state: "closed", weight: 7, draw: true }),
        }),
        el("span", { class: "nura-ready__check", staticHtml: icon("check", 22) }),
      ]),
      el("div", { class: "nura-ready__body" }, [
        statusBadge(transfer.status),
        el("p", {
          class: "nura-hero__value",
          text: formatMoney(transfer.amountMinor, transfer.currency, LOCALE),
        }),
        el("p", { class: "nura-lede", text: presentation.detail }),
      ]),
    ]);

    replaceChildren(region, [
      ready,

      el("dl", { class: "nura-meta" }, [
        metaItem("Sending bank", bankLabelLeading(state.tenant)),
        metaItem("Claim window", `${transfer.claimWindowHours} hours remaining`),
        metaItem("Bank reference", transfer.reference),
      ]),

      card({ title: "What happens next", headingLevel: "h2" }, [
        el("ol", { class: "nura-next-steps" }, [
          nextStep(
            "The claim link is placed in your message, and you send it yourself. Having the link starts a claim — it is never enough on its own to receive money.",
          ),
          nextStep(
            `The recipient's bank verifies them and collects whatever it needs. ${serviceLeading(state.tenant)} does not see those values.`,
          ),
          nextStep(
            "The banks settle. If nobody claims within the window, your bank expires the transfer and the money stays with you.",
          ),
        ]),
      ]),

      notice({
        tone: "info",
        title: `${serviceLeading(state.tenant)} holds no recipient details`,
        text: `There is no name, account or destination on this screen because ${serviceMid(state.tenant)} never receives them. The recipient identifies themselves to their own bank when they claim.`,
      }),

      referenceNote(
        state,
        "No claim link is generated. Generating and inserting one is deliberately absent while its behaviour in real browsers is still being validated.",
        "No claim link is generated.",
      ),
    ]);
  });

  const node = el("div", { class: "nura-stack" }, [
    stepIndicator({ steps: [...STEPS], current: 2 }),
    pageHeader(state.tenant.name, "Ready to claim"),
    region,
  ]);

  return {
    node,
    destroy(): void {
      live = false;
    },
  };
}
